import React, { useMemo, useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getBusinessUnitChart } from '@/services/api/dashboard';

type VisibleStatusKey = 'active' | 'idle' | 'lost' | 'inRepair' | 'broken';
type ChartStatusKey = VisibleStatusKey | 'capacity';

export interface BusinessUnitChartItem {
  label: string;
  totalUnits?: number;
  totalShare?: number;
  active: number;
  idle: number;
  lost: number;
  inRepair: number;
  broken: number;
}

interface BusinessUnitChartProps {
  data?: BusinessUnitChartItem[];
}

const DEFAULT_BUSINESS_UNIT_DATA: BusinessUnitChartItem[] = [
  { label: 'Outsourcing', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: 'Express', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: 'X-Dock', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: '4 Wheel', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: 'Quick Commerce', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: 'Scheduled Instant', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: 'Canvasser+Rental', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
  { label: 'Staging', totalUnits: 1302, totalShare: 30, active: 1, idle: 0, lost: 5, inRepair: 5, broken: 5 },
];

const STATUS_CONFIG: Record<ChartStatusKey, { label: string; color: string }> = {
  active: { label: 'Active', color: '#1FC16B' },
  idle: { label: 'Idle', color: '#F6B51E' },
  lost: { label: 'Lost', color: '#7B7B7B' },
  inRepair: { label: 'In Repair', color: '#5E2AAC' },
  broken: { label: 'Broken', color: '#FB3748' },
  capacity: { label: 'Capacity', color: '#F1F1F1' },
};

const STACK_ORDER: ChartStatusKey[] = ['inRepair', 'idle', 'lost', 'active', 'broken', 'capacity'];
const LEGEND_ORDER: VisibleStatusKey[] = ['active', 'idle', 'lost', 'inRepair', 'broken'];
const CHART_HEIGHT_PX = 324;
const MIN_SEGMENT_HEIGHT_PX = 16;
const ESTIMATED_PLOT_HEIGHT_PX = 260;

/** Converts an API snake_case/ALL_CAPS business unit key to a readable label.
 *  e.g. "CANVASSER_RENTAL" → "Canvasser Rental", "X_DOCK" → "X Dock" */
const formatBusinessUnitLabel = (raw: string): string =>
  raw
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const BusinessUnitChart: React.FC<BusinessUnitChartProps> = ({ data: propData }) => {
  const [apiData, setApiData] = useState<BusinessUnitChartItem[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const res = await getBusinessUnitChart();
        if (res?.data && Array.isArray(res.data)) {
          const mapped: BusinessUnitChartItem[] = res.data.map((item) => ({
            label: formatBusinessUnitLabel(item.businessUnit),
            totalUnits: item.totalVehicle,
            totalShare: item.percentageOfTotal,
            active: item.active,
            idle: item.idle,
            lost: item.lost,
            inRepair: item.inRepair,
            broken: item.broken,
          }));
          setApiData(mapped);
        }
      } catch (error) {
        console.error('Failed to fetch business unit chart data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, []);

  // Priority: prop data → api data → default (skeleton)
  const sourceData =
    (propData && propData.length > 0 ? propData : null) ??
    apiData ??
    DEFAULT_BUSINESS_UNIT_DATA;

  const chartData = useMemo(() => {
    const rawRows = sourceData.map((item) => {
      const statusTotal = item.active + item.idle + item.lost + item.inRepair + item.broken;

      return {
        ...item,
        statusTotal,
        totalUnits: item.totalUnits ?? statusTotal,
      };
    });

    const totalUnitsAcrossRows = rawRows.reduce((sum, item) => sum + item.totalUnits, 0);

    return rawRows.map((item) => ({
      ...item,
      totalShare:
        item.totalShare ??
        (totalUnitsAcrossRows > 0 ? (item.totalUnits / totalUnitsAcrossRows) * 100 : 0),
    }));
  }, [sourceData]);

  const chartMax = useMemo(() => {
    const highestTotal = chartData.reduce((max, item) => Math.max(max, item.statusTotal), 0);

    if (highestTotal <= 20) {
      return 20;
    }

    return Math.ceil(highestTotal / 5) * 5;
  }, [chartData]);

  // step=5 for small data (≤50), step=10 for larger — keeps tick count between 4-10
  const yAxisStep = useMemo(() => (chartMax <= 50 ? 5 : 10), [chartMax]);
  const yAxisTickAmount = useMemo(() => chartMax / yAxisStep, [chartMax, yAxisStep]);
  const minDisplayValue = useMemo(
    () => Math.max((MIN_SEGMENT_HEIGHT_PX / ESTIMATED_PLOT_HEIGHT_PX) * chartMax, 1),
    [chartMax]
  );

  const categories = useMemo(() => chartData.map((item) => item.label), [chartData]);

  const displayChartData = useMemo(
    () =>
      chartData.map((item) => {
        const displayValues: Record<VisibleStatusKey, number> = {
          active: item.active > 0 ? Math.max(item.active, minDisplayValue) : 0,
          idle: item.idle > 0 ? Math.max(item.idle, minDisplayValue) : 0,
          lost: item.lost > 0 ? Math.max(item.lost, minDisplayValue) : 0,
          inRepair: item.inRepair > 0 ? Math.max(item.inRepair, minDisplayValue) : 0,
          broken: item.broken > 0 ? Math.max(item.broken, minDisplayValue) : 0,
        };

        return {
          ...item,
          ...displayValues,
          displayStatusTotal:
            displayValues.active +
            displayValues.idle +
            displayValues.lost +
            displayValues.inRepair +
            displayValues.broken,
        };
      }),
    [chartData, minDisplayValue]
  );

  const series = useMemo(
    () =>
      STACK_ORDER.map((statusKey) => ({
        name: STATUS_CONFIG[statusKey].label,
        data: displayChartData.map((item) => {
          if (statusKey === 'capacity') {
            return Math.max(chartMax - item.displayStatusTotal, 0);
          }

          return item[statusKey];
        }),
      })),
    [chartMax, displayChartData]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        offsetY: -6,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      colors: STACK_ORDER.map((statusKey) => STATUS_CONFIG[statusKey].color),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '88%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#FFFFFF'],
      },
      legend: {
        show: false,
      },
      grid: {
        borderColor: '#E6E6E6',
        strokeDashArray: 0,
        padding: {
          top: 0,
          left: 28,
          right: 10,
          bottom: 8,
        },
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      xaxis: {
        categories: categories as unknown as string[],
        crosshairs: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: false,
          trim: false,
          hideOverlappingLabels: false,
          maxHeight: 72,
          offsetY: 2,
          style: {
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            colors: chartData.map(() => '#5C5C5C'),
          },
        },
      },
      yaxis: {
        min: 0,
        max: chartMax,
        tickAmount: yAxisTickAmount,
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          align: 'left',
          formatter: (value) => {
            const roundedValue = Math.round(value);
            return roundedValue % yAxisStep === 0 ? roundedValue.toString() : '';
          },
          offsetX: 8,
          minWidth: 28,
          maxWidth: 28,
          style: {
            colors: ['#737373'],
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          },
        },
      },
      tooltip: {
        enabled: true,
        enabledOnSeries: [0, 1, 2, 3, 4],
        shared: false,
        custom: ({ seriesIndex, dataPointIndex }) => {
          const currentSeries = series[seriesIndex];
          const currentRow = chartData[dataPointIndex];
          const currentStatusKey = STACK_ORDER[seriesIndex];
          const value =
            currentStatusKey === 'capacity'
              ? 0
              : Number(currentRow?.[currentStatusKey] ?? 0);
          const percentage = currentRow?.statusTotal
            ? Math.round((value / currentRow.statusTotal) * 100)
            : 0;

          return `
            <div class="bg-white p-3 rounded-lg shadow-lg">
              <div style="color: #A3A3A3; font-size: 12px; font-weight: 500; line-height: 16px;">${currentSeries.name}</div>
              <div class="flex items-center gap-2 mt-1">
                <span style="color: #171717; font-weight: 500; font-size: 18px; line-height: 24px; letter-spacing: -0.015em;">${value.toLocaleString()} (${percentage}%)</span>
              </div>
            </div>
          `;
        },
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
    }),
    [categories, chartData, chartMax, series, yAxisStep, yAxisTickAmount]
  );

  // Only show legend items that have at least one non-zero value across all data
  const visibleLegendKeys = useMemo(
    () => LEGEND_ORDER.filter((statusKey) => chartData.some((item) => item[statusKey] > 0)),
    [chartData]
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-6 pt-5 pb-4 shadow-sm">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <div className="w-10 h-10 flex items-center justify-center">
          <i className="ri-bar-chart-box-line text-lg text-neutral-900" />
        </div>
        <h3
          className="text-lg font-medium text-gray-900"
          style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}
        >
          Business Unit
        </h3>
      </div>

      {loading ? (
        <div className="flex items-center justify-center" style={{ height: CHART_HEIGHT_PX }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <div className="business-unit-chart-container">
          <style>
            {`
              .business-unit-chart-container .apexcharts-yaxis line,
              .business-unit-chart-container .apexcharts-yaxis path {
                display: none !important;
              }
            `}
          </style>
          <Chart options={chartOptions} series={series} type="bar" height={CHART_HEIGHT_PX} />

          <div
            className="-mt-7 pl-[52px] pr-[10px]"
            style={{ pointerEvents: 'none' }}
          >
            <div
              className="grid gap-0"
              style={{
                gridTemplateColumns: `repeat(${chartData.length}, minmax(0, 1fr))`,
                pointerEvents: 'none',
              }}
            >
              {chartData.map((item) => (
                <div
                  key={item.label}
                  className="px-1 text-center"
                  style={{ pointerEvents: 'none' }}
                >
                  <div
                    className="text-[14px] font-normal text-neutral-600"
                    style={{
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      lineHeight: '20px',
                      letterSpacing: '0',
                      pointerEvents: 'none',
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="mt-1 text-[12px] font-normal text-neutral-400"
                    style={{
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      lineHeight: '16px',
                      letterSpacing: '0',
                      pointerEvents: 'none',
                    }}
                  >
                    {item.totalUnits.toLocaleString('en-US')} ({item.totalShare}%)
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`mt-4 flex flex-wrap items-center justify-center gap-x-11 gap-y-3 ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
        {visibleLegendKeys.map((statusKey) => (
          <div key={statusKey} className="flex items-center gap-3">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{
                backgroundColor: STATUS_CONFIG[statusKey].color,
                border: '2px solid white',
                boxShadow: '0px 2px 4px 0px #1B1C1D0A',
              }}
            />
            <span
              className="text-[16px] font-normal text-[#5C5C5C]"
              style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}
            >
              {STATUS_CONFIG[statusKey].label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BusinessUnitChart;

